/**
 * @file     useSharedGeolocation.ts
 * @purpose  React hook that exposes a single shared geolocation state (permission + coords + classification) across the app. All heavy work deferred to idle time to protect LCP.
 */

'use client';

import { useEffect, useState, useCallback } from 'react';
import {
  readStoredGeoPermission,
  writeStoredGeoPermission,
  readStoredCoords,
  writeStoredCoords,
  scheduleIdle,
  type GeoPermission,
} from '@/utils/geo/sharedGeolocation';
import { classifyLocation, type LocationClassification } from '@/utils/geo/classifyLocation';

export type GeoState = {
  permission: GeoPermission;
  coords: { lng: number; lat: number } | null;
  classification: LocationClassification | null;
  busy: boolean;
};

export type FindMyAreaResult =
  | { ok: true; coords: { lng: number; lat: number } }
  | { ok: false; reason: 'denied' | 'unavailable' | 'timeout' };

type Listener = (s: GeoState) => void;

// Module-level store, a single source of truth shared by every component that calls the hook.
// Any component calling useSharedGeolocation sees the same permission/coords/classification.
const store: GeoState = {
  permission: null,
  coords: null,
  classification: null,
  busy: false,
};
const listeners = new Set<Listener>();

function setState(patch: Partial<GeoState>): void {
  Object.assign(store, patch);
  listeners.forEach((l) => l({ ...store }));
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

let hydrated = false;
function hydrateFromStorage(): void {
  if (hydrated || typeof window === 'undefined') return;
  hydrated = true;
  const perm = readStoredGeoPermission();
  const coords = readStoredCoords();
  setState({
    permission: perm,
    coords: coords ? { lng: coords.lng, lat: coords.lat } : null,
  });
}

function callGetCurrentPosition(
  onSuccess: (c: { lng: number; lat: number }) => void,
  onError: (err: GeolocationPositionError) => void,
): void {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    onError({ code: 2, message: 'unsupported', PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3 } as GeolocationPositionError);
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => onSuccess({ lng: pos.coords.longitude, lat: pos.coords.latitude }),
    onError,
    { timeout: 6000, maximumAge: 300000 },
  );
}

async function runClassification(lng: number, lat: number): Promise<void> {
  const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
  const classification = await classifyLocation(lng, lat, token);
  setState({ classification });
}

function requestPosition(opts: { silent: boolean }): Promise<FindMyAreaResult> {
  return new Promise((resolve) => {
    setState({ busy: true });
    callGetCurrentPosition(
      (c) => {
        writeStoredGeoPermission('granted');
        writeStoredCoords(c);
        setState({ permission: 'granted', coords: c, busy: false });
        // Classification is not on the critical path, defer it.
        scheduleIdle(() => {
          void runClassification(c.lng, c.lat);
        });
        resolve({ ok: true, coords: c });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          writeStoredGeoPermission('denied');
          setState({ permission: 'denied', busy: false });
        } else {
          setState({ busy: false });
        }
        // Silent requests swallow errors; user-initiated ones resolve too, callers inspect state.
        void opts;
        resolve({
          ok: false,
          reason:
            err.code === err.PERMISSION_DENIED
              ? 'denied'
              : err.code === err.TIMEOUT
                ? 'timeout'
                : 'unavailable',
        });
      },
    );
  });
}

// Initialize the shared state once per session. Deferred to idle so it never blocks FCP/LCP.
// Callable from components, the internal guard prevents repeated work.
let initialized = false;
export function initSharedGeolocation(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  hydrateFromStorage();

  scheduleIdle(() => {
    // Auto-classify ONLY when we already have cached coords from a prior
    // explicit grant. We deliberately do NOT call getCurrentPosition here —
    // on iOS Safari the "Allow Once" grant is session-scoped and expires
    // when the tab closes, so any silent re-fetch on subsequent visits
    // surfaces a fresh permission prompt to the user. The shared store
    // stays passive; explicit user actions (e.g. "Find my area" button)
    // are what trigger location requests via findMyArea / requestPosition.
    if (store.coords) {
      void runClassification(store.coords.lng, store.coords.lat);
      return;
    }
    // Refresh the cached permission state from the Permissions API so the
    // UI shows the right affordances (e.g. button enabled state), but do
    // not request a position — that would prompt.
    if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((status) => {
          if (status.state === 'granted') {
            writeStoredGeoPermission('granted');
            setState({ permission: 'granted' });
          } else if (status.state === 'denied') {
            writeStoredGeoPermission('denied');
            setState({ permission: 'denied' });
          }
          // 'prompt', do nothing. Wait for user-initiated action.
        })
        .catch(() => {
          // Permissions API unavailable, leave state untouched.
        });
    }
  });
}

export function useSharedGeolocation(): GeoState & {
  findMyArea: () => Promise<FindMyAreaResult>;
} {
  const [state, setLocalState] = useState<GeoState>({ ...store });

  useEffect(() => {
    initSharedGeolocation();
    setLocalState({ ...store });
    return subscribe(setLocalState);
  }, []);

  const findMyArea = useCallback(async () => {
    return requestPosition({ silent: false });
  }, []);

  return { ...state, findMyArea };
}
